import { INSTRUCTION_LRS } from '@packages/domain/constants/instructions';
import type { Price } from '@packages/domain/models/price';
import {
  AED_CARD_PAYMENT_OPTIONS,
  AED_CARD_WARNING_PORTALS,
  BILL_PAY_PAYMENT_METHOD,
  BPAY_PAYMENT_METHOD,
  EMAIL_PAYMENT_METHOD,
  INDIAN_PARTNER_AHALIA,
  INDIAN_PARTNER_GSTIN_AHALIA,
  INDIAN_PARTNER_GSTIN_LULU,
  INDIAN_PARTNER_LICENCE_NO_AHALIA,
  INDIAN_PARTNER_LICENCE_NO_LULU,
  INDIAN_PARTNER_LULU,
  InstructionsType,
  LULU_EMAIL,
  PAYNOW_PAYMENT_OPTIONS,
  QR_PAYMENT_METHOD,
} from 'constants/offers';
import get from 'lodash/get';
import type { Field } from 'models/field';
import type { Offer, OfferWithIds } from 'models/offer';
import type { RootState } from 'reducers/types';
import { getInstructionByName } from 'selectors/entities/instructions/instructions';
import { getOrderOffer } from 'selectors/entities/order/order';
import {
  getPaymentInstruction,
  getPaymentOffer,
  getPaymentOfferFromPayment,
} from 'selectors/entities/payment/payment';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import {
  getSelectedOffer,
  getSelectedOfferId,
  isEmbedded,
} from 'selectors/ui/ui';
import { isStablecoin } from 'utils/currency/currency';
import { getLocalCurrency } from '../currencies/currencies';
import { getFieldById } from '../fields/fields';

const NO_FIELDS: Field[] = [];

export const getOffer = (state: RootState, id: string) => {
  const offer = findOffer(state, id);

  if (!offer) {
    return {} as Offer;
  }

  return buildOfferWithEntities(state, offer);
};

export const getOfferName = (state: RootState, id: string) => {
  const offer = findOffer(state, id);

  return offer ? offer.name : '';
};

export const getLocalOffers = (state: RootState) => {
  const offers = getSingleOffers(state);
  const { code: localCurrency } = getLocalCurrency(state);

  return offers.filter(({ price }) => {
    return price.currency === localCurrency || isStablecoin(price.currency);
  });
};

export const allSingleOffersAreOnline = (state: RootState) => {
  const offers = getSingleOffers(state);

  return offers.every(offer => offer.paymentMethod !== 'bank_transfer');
};

export const allRecurringOffersAreOnline = (state: RootState) => {
  const offers = getRecurringOffers(state);

  return offers.every(offer => offer.paymentMethod !== 'bank_transfer');
};

export const allOffersAreOnline = (state: RootState) => {
  return allSingleOffersAreOnline(state) && allRecurringOffersAreOnline(state);
};

export const getForeignOffers = (state: RootState) => {
  const offers = getSingleOffers(state);
  const { code: localCurrency } = getLocalCurrency(state);

  return offers.filter(({ price }) => {
    return price.currency !== localCurrency && !isStablecoin(price.currency);
  });
};

export const isLulu = (state: RootState) => {
  const { value = '' } = getPaymentInstruction(state, INSTRUCTION_LRS) || {};
  return value.includes(LULU_EMAIL);
};

const INDIAN_PARTNERS = ['lulu', 'ahal'] as const;
type IndianPartnerType = (typeof INDIAN_PARTNERS)[number];
const INDIAN_PARTNERS_DATA: Record<
  IndianPartnerType,
  {
    indianPartner: string;
    indianPartnerGstin: string;
    indianPartnerLicenceNo: string;
  }
> = {
  lulu: {
    indianPartner: INDIAN_PARTNER_LULU,
    indianPartnerGstin: INDIAN_PARTNER_GSTIN_LULU,
    indianPartnerLicenceNo: INDIAN_PARTNER_LICENCE_NO_LULU,
  },
  ahal: {
    indianPartner: INDIAN_PARTNER_AHALIA,
    indianPartnerGstin: INDIAN_PARTNER_GSTIN_AHALIA,
    indianPartnerLicenceNo: INDIAN_PARTNER_LICENCE_NO_AHALIA,
  },
};

const NO_INDIAN_PARTNER_DATA = {};

export const getIndianPartnerData = (paymentProcessor: string) => {
  if (!paymentProcessor) {
    return NO_INDIAN_PARTNER_DATA;
  }

  const paymentProcessorLower = paymentProcessor.toLowerCase();
  const partner = INDIAN_PARTNERS.find(partner =>
    paymentProcessorLower.includes(partner),
  );

  if (partner) {
    return INDIAN_PARTNERS_DATA[partner];
  }

  return NO_INDIAN_PARTNER_DATA;
};

export const indianPartnerData = (state: RootState) => {
  const { paymentMethodProcessor } = getOrderOffer(state);

  return getIndianPartnerData(paymentMethodProcessor);
};

export const isUsingDocCollectorLRS = (state: RootState) => {
  const { paymentOption } = getPaymentOfferFromPayment(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('CASHFREE');
};

export const isLuluOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('LULU');
};

export const isCashfreeOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);

  if (!paymentOption) {
    return false;
  }

  return paymentOption.toUpperCase().includes('CASHFREE');
};

export const isHdfcOnlineOfferSelected = (state: RootState) => {
  const { paymentOption } = getOrderOffer(state);
  if (!paymentOption) {
    return false;
  }
  const normalized = paymentOption.toUpperCase();
  return normalized.includes('HDFC') && normalized.includes('ONLINE');
};

export const isDirectDebitOfferSelected = (state: RootState) => {
  const offer = getSelectedOffer(state);

  return offer?.paymentMethod === 'direct_debit';
};

export const isOppOfferSelected = (state: RootState) => {
  return /opp/i.test(getPaymentOffer(state)?.paymentOption ?? '');
};

export const isUpopOfferSelected = (state: RootState) => {
  const upopPattern = /opp.*unionpay/i;
  const selectedOption =
    (selectedSingleOffer(state) ?? selectedRecurringOffer(state))
      ?.paymentOption ?? '';
  const paymentOption = getPaymentOffer(state)?.paymentOption ?? '';

  return upopPattern.test(selectedOption) || upopPattern.test(paymentOption);
};

export const getRepeatPaymentOffer = (state: RootState) => {
  return get(state, 'entities.repeatPaymentOffer');
};

export const shouldShowEURateDisclaimer = (state: RootState) => {
  const {
    entities: {
      offers: { byId: offers, ids },
    },
  } = state;

  const hasMarkupRate = (id: string) => !!offers[id].markupRate;
  return ids.some(hasMarkupRate);
};

export function forceRedirectInNewWindow(state: RootState) {
  const paymentIsEmbedded = isEmbedded(state);
  const offer = getSelectedOffer(state);
  const redirectUrl = getInstructionByName(state, 'redirect_url') || {};
  const instructionsTypes =
    getInstructionByName(state, 'type')?.value.split(',') || [];

  if (!('value' in redirectUrl)) {
    return false;
  }

  if (paymentIsEmbedded && !instructionsTypes.includes('embedded')) {
    return true;
  }

  return (
    paymentIsEmbedded &&
    ((['astropay_br_boleto'].includes(offer?.paymentOption) &&
      offer?.paymentMethod === 'bank_transfer') ||
      (offer?.paymentOption === 'dlocal_cop_pse' &&
        offer?.paymentMethod === 'online'))
  );
}

export function getInstructionsType(state: RootState) {
  const { paymentMethodInternalName, paymentOption } =
    getPaymentOfferFromPayment(state);
  const redirectURL = getInstructionByName(state, 'redirect_url');
  const embeddedScript = getInstructionByName(state, 'embedded_script');
  const instructionsTypes =
    getInstructionByName(state, 'type')?.value.split(',') || [];

  if (QR_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.QR;
  }

  if (EMAIL_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.EMAIL;
  }

  if (BPAY_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.BPAY;
  }

  if (BILL_PAY_PAYMENT_METHOD.includes(paymentMethodInternalName)) {
    return InstructionsType.BILLPAY;
  }

  if (PAYNOW_PAYMENT_OPTIONS.includes(paymentOption)) {
    return InstructionsType.PAYNOW;
  }

  if (redirectURL && !embeddedScript) {
    return InstructionsType.REDIRECT;
  }
  if (instructionsTypes.includes('script')) {
    return InstructionsType.SCRIPT;
  }

  return InstructionsType.EMPTY;
}

export const getSingleOffers = (state: RootState) => {
  const {
    entities: { offers: { ids } = {} },
  } = state;

  return ids?.map(id => getSingleOffer(state, id)) || [];
};

export const getRecurringOffers = (state: RootState) => {
  const {
    entities: { recurringOffers: { ids } = {} },
  } = state;

  return ids?.map(id => getRecurringOffer(state, id)) || [];
};

export const getRecurringOffer = (state: RootState, id: string) => {
  const {
    entities: {
      recurringOffers: { byId },
    },
  } = state;

  return buildOfferWithEntities(state, byId[id]);
};

export const findOffer = (state: RootState, id: string) => {
  const {
    entities: {
      offers: { byId: singleOffersById } = {},
      recurringOffers: { byId: recurringOffersById } = {},
    },
  } = state;

  return recurringOffersById?.[id]
    ? recurringOffersById[id]
    : singleOffersById?.[id];
};

export const getOffersRecurringConfigurationId = (state: RootState) => {
  return state.entities.recurringOffers.metadata.configurationId;
};

export const findSingleOffer = (state: RootState, id: string) => {
  const {
    entities: { offers: { byId: singleOffersById } = {} },
  } = state;

  return singleOffersById?.[id];
};

export const selectedSingleOffer = (state: RootState) => {
  const selectedOfferId = getSelectedOfferId(state);
  const {
    entities: { offers: { byId: singleOffersById } = {} },
  } = state;

  return singleOffersById?.[selectedOfferId];
};

export const findRecurringOffer = (state: RootState, id: string) => {
  const {
    entities: { recurringOffers: { byId: recurringOffersById } = {} },
  } = state;

  return recurringOffersById?.[id];
};

export const selectedRecurringOffer = (state: RootState) => {
  const selectedOfferId = getSelectedOfferId(state);
  const {
    entities: { recurringOffers: { byId: recurringOffersById } = {} },
  } = state;

  return recurringOffersById?.[selectedOfferId];
};

const getFee = (offer: Offer, price: Price<string>) => {
  const { currency } = price;
  const { fees: value } = offer;

  return { currency, value };
};

const getSingleOffer = (state: RootState, id: string) => {
  const {
    entities: {
      offers: { byId },
    },
  } = state;

  return buildOfferWithEntities(state, byId[id]);
};

const buildOfferWithEntities = (state: RootState, offer: OfferWithIds) => {
  const {
    entities: { prices },
  } = state;

  const price = prices[offer.price];
  const fee = getFee(offer, price);
  const fields = (offer.fields || NO_FIELDS).map(field =>
    getFieldById(state, field),
  );
  const recipientId = getRecipientId(state);
  const requiresAedWarning =
    AED_CARD_WARNING_PORTALS.includes(recipientId) &&
    AED_CARD_PAYMENT_OPTIONS.includes(offer.paymentOption);

  return { ...offer, price, fee, fields, requiresAedWarning };
};

export const getRecurringOffersMetadata = (state: RootState) => {
  return state.entities.recurringOffers.metadata;
};
